import * as React from "react"

import { cn } from "../../lib/utils.js"

/**
 * THE way to render a date or time input in this app.
 *
 * iOS Safari gives `input[type=date]` and `input[type=time]` an intrinsic width from their own
 * shadow DOM and will NOT shrink below it. `w-full`, `min-w-0`, `appearance-none`, `flex-1`,
 * stacking the fields and shrinking the font have all been tried — the control simply refuses
 * to be narrower than its content, so it runs off the right edge of a phone and drags the
 * surrounding layout with it.
 *
 * The fix is to take the native input out of layout flow: `absolute inset-0 opacity-0`, sitting
 * on top of a bordered box we draw ourselves. Our box decides the width, so the input's
 * intrinsic size can no longer influence anything. A tap anywhere in the box still lands on the
 * real input, so the native picker opens exactly as before, and typing into it still works
 * because the input is focusable — only invisible.
 *
 * Because the value is rendered by us rather than by the control, we also control the format
 * instead of inheriting the browser's locale rendering.
 *
 * `Input` delegates here automatically for `type="date"` and `type="time"`, so call sites do not
 * need to know about any of this. Do not "simplify" it back to a plain styled `<input>`: it
 * looks identical in a desktop browser and breaks on every phone.
 *
 * The overlay is for touch devices only. With a mouse and keyboard the hidden input is unusable:
 * a desktop browser does not open the picker when the field body is clicked (only its calendar
 * icon does, and that is invisible too), and typing shows no caret or segments — which is how a
 * date of birth decades back actually gets entered. So on `(hover: hover) and (pointer: fine)`
 * the real input is shown inside the box and our rendered value steps aside. Desktop has no
 * intrinsic-width problem, and the input stays `absolute`, so layout is identical either way.
 *
 * Both a controlled (`value` + `onChange`) and an uncontrolled (`defaultValue`) caller work.
 * The field used to pass `value ?? ""` to the input either way, which pinned every uncontrolled
 * call site to empty: the picker set a date and React wiped it on the next render. When there is
 * no `value` prop the input owns its value and we only mirror it for the drawn display.
 *
 * Verify changes with a hit test, not a screenshot — `document.elementFromPoint` at the centre
 * of the box must return the input. A screenshot cannot tell you the picker still opens.
 */
export interface NativePickerFieldProps
  extends Omit<React.ComponentProps<"input">, "type"> {
  type?: "date" | "time"
  /** Applied to the drawn box, which is what owns layout — not to the hidden input. */
  className?: string
  invalid?: boolean
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
  /** Shown when there is no value. */
  placeholder?: string
  inputRef?: React.Ref<HTMLInputElement>
}

export const NativePickerField = React.forwardRef<HTMLInputElement, NativePickerFieldProps>(
  (
    { type = "date", value, className, invalid, icon: Icon, placeholder = "—", inputRef, disabled, ...props },
    ref,
  ) => {
    // An uncontrolled caller ("defaultValue", no "value") keeps its value in the DOM; we track it
    // only so the box we draw shows what the input holds on the touch path.
    const controlled = value !== undefined
    const [ownValue, setOwnValue] = React.useState(() =>
      controlled ? "" : String(props.defaultValue ?? ""),
    )
    const current = controlled ? value : ownValue
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!controlled) setOwnValue(event.target.value)
      props.onChange?.(event)
    }
    const display =
      type === "time" ? formatTimeDisplay(current) : formatDateDisplay(current)

    return (
      <div
        className={cn(
          // Matches Input's resting appearance so swapped call sites look unchanged.
          "relative flex h-10 w-full min-w-0 items-center overflow-hidden rounded-md",
          "border border-input bg-card px-3 py-2 text-base text-foreground md:text-sm",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset",
          disabled && "pointer-events-none opacity-50",
          invalid && "border-destructive focus-within:ring-destructive",
          className,
        )}
      >
        <span
          aria-hidden
          className="min-w-0 flex-1 truncate [@media(hover:hover)_and_(pointer:fine)]:invisible"
        >
          {display || <span className="text-muted-foreground">{placeholder}</span>}
        </span>
        {Icon ? (
          <Icon
            aria-hidden
            className="ml-2 h-4 w-4 shrink-0 text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:invisible"
          />
        ) : null}
        <input
          {...props}
          ref={inputRef ?? ref}
          type={type}
          {...(controlled ? { value: value ?? "" } : {})}
          onChange={handleChange}
          disabled={disabled}
          aria-invalid={invalid || props["aria-invalid"]}
          className={cn(
            "absolute inset-0 h-full w-full cursor-pointer opacity-0",
            // Mouse-and-keyboard devices get the real, typeable control — see above.
            "[@media(hover:hover)_and_(pointer:fine)]:cursor-text [@media(hover:hover)_and_(pointer:fine)]:bg-transparent [@media(hover:hover)_and_(pointer:fine)]:px-3 [@media(hover:hover)_and_(pointer:fine)]:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:outline-none",
          )}
        />
      </div>
    )
  },
)
NativePickerField.displayName = "NativePickerField"

/** `YYYY-MM-DD` → `4 Oct 1987`. Midday avoids the value shifting a day across timezones. */
export function formatDateDisplay(value: unknown): string {
  if (!value) return ""
  const d = new Date(`${String(value)}T12:00:00+10:00`)
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString("en-AU", {
        timeZone: "Australia/Brisbane",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
}

/** `14:30` → `2:30 pm`. */
export function formatTimeDisplay(value: unknown): string {
  if (!value) return ""
  const [h, m] = String(value).split(":").map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return String(value)
  const suffix = h >= 12 ? "pm" : "am"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`
}

export default NativePickerField
